import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiChevronRight,
  HiStar,
  HiOutlineStar,
  HiOutlineClock,
  HiOutlineBuildingStorefront,
  HiOutlineDocumentText,
  HiOutlinePhoto,
  HiOutlineUserPlus,
  HiOutlineCalendarDays,
  HiOutlineMagnifyingGlass,
  HiOutlineTag,
  HiOutlineKey,
  HiOutlineEyeSlash,
  HiOutlineTrash,
  HiOutlineShoppingBag,
  HiOutlineBanknotes,
  HiOutlineMegaphone,
  HiOutlineTicket,
  HiOutlineRectangleGroup,
  HiOutlineBellAlert,
  HiOutlineNewspaper,
  HiOutlineChartBar,
  HiOutlineGift,
  HiOutlinePencilSquare,
  HiOutlineUserMinus,
  HiOutlineChatBubbleLeftRight,
  HiOutlineChatBubbleBottomCenterText,
  HiOutlineLightBulb,
  HiOutlineEnvelope,
  HiOutlineArrowPath,
} from "react-icons/hi2";

import { useAdminSearch } from "../components/admin-search-context";

type IconComponent = React.ComponentType<{ className?: string }>;

interface MenuItem {
  label: string;
  path: string;
  highlight?: boolean;
  icon: IconComponent;
}

interface MenuGroup {
  title: string;
  groupIcon: IconComponent;
  groupColor: string; // bg-xxx text-white
  items: MenuItem[];
}

const menuGroups: MenuGroup[] = [
  {
    title: "매장 관리",
    groupIcon: HiOutlineBuildingStorefront,
    groupColor: "bg-primary text-white",
    items: [
      { label: "매장 입점신청", path: "/admin/store-signup-request", icon: HiOutlineDocumentText },
      { label: "매장 관리 (이미지·토스 재인증)", path: "/admin/store-management", icon: HiOutlinePhoto },
      { label: "매니저 계정 생성", path: "/admin/manager-create", icon: HiOutlineUserPlus },
      { label: "수수료 무료 기간 설정", path: "/admin/fee-free-until", icon: HiOutlineCalendarDays },
      { label: "매장 검색 키워드 관리", path: "/admin/store-search-keyword", icon: HiOutlineMagnifyingGlass },
      { label: "예약팩 할인율 설정", path: "/admin/pack-discount-rate-tier", icon: HiOutlineTag },
      { label: "예약팩 주문 수량 제한 설정", path: "/admin/pack-order-limit-config", icon: HiOutlineTag },
      { label: "매장 예약 시간 조회", path: "/admin/store-reserve-time", icon: HiOutlineClock },
      { label: "매장 마스터 로그인", path: "/admin/master-login", icon: HiOutlineKey },
      { label: "비활성화 매장 조회", path: "/admin/inactive-stores", icon: HiOutlineEyeSlash },
      { label: "매장 미노출 처리", path: "/admin/store-hide", icon: HiOutlineEyeSlash },
      { label: "매장 삭제처리", path: "/admin/store-delete", icon: HiOutlineTrash },
    ],
  },
  {
    title: "주문",
    groupIcon: HiOutlineShoppingBag,
    groupColor: "bg-purple-500 text-white",
    items: [
      { label: "주문 취소 (팩)", path: "/admin/cancel-order", icon: HiOutlineShoppingBag },
      { label: "주문 취소 (실시간)", path: "/admin/cancel-live-order", icon: HiOutlineShoppingBag },
      { label: "주문 부분환불", path: "/admin/partial-refund-order", icon: HiOutlineBanknotes },
      {
        label: "예약 판매 비정상 주문 자동확정",
        path: "/admin/confirm-pre-order-as-auto-confirm",
        highlight: true,
        icon: HiOutlineBellAlert,
      },
    ],
  },
  {
    title: "정산",
    groupIcon: HiOutlineBanknotes,
    groupColor: "bg-green-500 text-white",
    items: [
      { label: "전체관리 (월별)", path: "/admin/monthly-overview", icon: HiOutlineChartBar },
      { label: "선수금 장부", path: "/admin/advance-ledger", icon: HiOutlineBanknotes },
      { label: "정산 지급 관리", path: "/admin/payout-management", icon: HiOutlineBanknotes },
      {
        label: "토스 지급 상태 동기화",
        path: "/admin/toss-payout-sync",
        icon: HiOutlineArrowPath,
      },
      { label: "이번달 정산 미리보기", path: "/admin/payout-preview", icon: HiOutlineChartBar },
      { label: "정산 매칭 (홈택스 XLSX)", path: "/admin/settlement-match", icon: HiOutlineDocumentText },
      { label: "정산요청 관리", path: "/admin/settle-management", icon: HiOutlineBanknotes },
      { label: "수동 정산요청", path: "/admin/settle-management", icon: HiOutlineBanknotes },
      { label: "레거시 정산 전용", path: "/admin/legacy-settle-management", icon: HiOutlineBanknotes },
      { label: "레거시 정산 정보 수정", path: "/admin/legacy-settlement-info-edit", icon: HiOutlinePencilSquare },
    ],
  },
  {
    title: "마케팅 및 운영",
    groupIcon: HiOutlineMegaphone,
    groupColor: "bg-yellow-500 text-white",
    items: [
      { label: "쿠폰 발행", path: "/admin/issue-coupon", icon: HiOutlineTicket },
      { label: "배너 관리", path: "/admin/banner-management", icon: HiOutlineRectangleGroup },
      { label: "새로운 매장 푸시 알림", path: "/admin/new-store-push-alarm", icon: HiOutlineBellAlert },
      { label: "사장님 푸시 알림", path: "/admin/seller-push-alarm", icon: HiOutlineBellAlert },
      { label: "사장님 정기 푸시 제외", path: "/admin/seller-scheduled-push-opt-out", icon: HiOutlineBellAlert },
      { label: "사장님 공지 관리", path: "/admin/boss-notice", icon: HiOutlineNewspaper },
      { label: "프리퀀시 모니터링", path: "/admin/frequency-monitoring", icon: HiOutlineChartBar },
      { label: "매장 쿠폰 발행", path: "/admin/store-coupon-issue", icon: HiOutlineGift },
      { label: "유저 이름 변경", path: "/admin/user-name-change", icon: HiOutlinePencilSquare },
      { label: "유저 삭제", path: "/admin/user-delete", icon: HiOutlineUserMinus },
    ],
  },
  {
    title: "커뮤니티 및 기타",
    groupIcon: HiOutlineChatBubbleLeftRight,
    groupColor: "bg-gray-500 text-white",
    items: [
      { label: "매장 리뷰", path: "/admin/store-review", icon: HiOutlineStar },
      { label: "커뮤니티 피드백 관리", path: "/admin/community", icon: HiOutlineChatBubbleBottomCenterText },
      { label: "사장님 의견 관리", path: "/admin/boss-feedback", icon: HiOutlineLightBulb },
      { label: "매장 메시지(솔라피) 확인", path: "/admin/what-message-they-receive", icon: HiOutlineEnvelope },
    ],
  },
];

const RECENT_MENUS_KEY = "admin_recent_menus";
const MAX_RECENT_MENUS = 8;
const FAVORITE_MENUS_KEY = "admin_favorite_menus";

function getFavoritePaths(): string[] {
  try {
    const saved = localStorage.getItem(FAVORITE_MENUS_KEY);
    if (!saved) return [];
    const parsed = JSON.parse(saved) as unknown;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function getRecentPaths(): string[] {
  try {
    const saved = localStorage.getItem(RECENT_MENUS_KEY);
    if (!saved) return [];
    const parsed = JSON.parse(saved) as unknown;
    return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENT_MENUS) : [];
  } catch {
    return [];
  }
}

const AdminHome = () => {
  const navigate = useNavigate();
  const { searchQuery } = useAdminSearch();
  const [recentPaths, setRecentPaths] = useState<string[]>(getRecentPaths);
  const [favoritePaths, setFavoritePaths] = useState<string[]>(getFavoritePaths);

  const pathToItem = useMemo(() => {
    const map = new Map<string, MenuItem>();
    for (const group of menuGroups) {
      for (const item of group.items) {
        map.set(item.path, item);
      }
    }
    return map;
  }, []);

  const handleMenuClick = (item: MenuItem) => {
    const saved = getRecentPaths();
    const next = [item.path, ...saved.filter((p) => p !== item.path)].slice(
      0,
      MAX_RECENT_MENUS,
    );
    localStorage.setItem(RECENT_MENUS_KEY, JSON.stringify(next));
    setRecentPaths(next);
    navigate(item.path);
  };

  const toggleFavorite = (e: React.MouseEvent, path: string) => {
    e.stopPropagation();
    setFavoritePaths((prev) => {
      const isFav = prev.includes(path);
      const next = isFav ? prev.filter((p) => p !== path) : [...prev, path];
      localStorage.setItem(FAVORITE_MENUS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return menuGroups;
    return menuGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => item.label.toLowerCase().includes(q)),
      }))
      .filter((group) => group.items.length > 0);
  }, [searchQuery]);

  return (
    <div className="mx-auto max-w-6xl">
      {/* 즐겨찾기 메뉴 */}
        {favoritePaths.length > 0 && (
          <section className="mb-3 flex items-center gap-3">
            <h2 className="flex shrink-0 items-center gap-1 text-xs font-semibold text-gray-500">
              <HiStar className="h-3.5 w-3.5 text-yellow-400" />
              즐겨찾기
            </h2>
            <div className="h-3 w-px shrink-0 bg-gray-300" />
            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
              {favoritePaths.map((path) => {
                const item = pathToItem.get(path);
                if (!item) return null;
                const ItemIcon = item.icon;
                return (
                  <button
                    key={path}
                    type="button"
                    onClick={() => handleMenuClick(item)}
                    className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-left shadow-sm transition hover:border-yellow-400 hover:bg-yellow-50/50"
                  >
                    <ItemIcon className="h-3.5 w-3.5 text-yellow-500" />
                    <span className="text-xs font-medium text-gray-700">
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* 최근 방문 메뉴 */}
        {recentPaths.length > 0 && (
          <section className="mb-8 flex items-center gap-3">
            <h2 className="flex shrink-0 items-center gap-1 text-xs font-semibold text-gray-500">
              <HiOutlineClock className="h-3.5 w-3.5 text-blue-500" />
              최근방문
            </h2>
            <div className="h-3 w-px shrink-0 bg-gray-300" />
            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
              {recentPaths.map((path) => {
                const item = pathToItem.get(path);
                if (!item) return null;
                const ItemIcon = item.icon;
                return (
                  <button
                    key={path}
                    type="button"
                    onClick={() => handleMenuClick(item)}
                    className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-left shadow-sm transition hover:border-blue-400 hover:bg-blue-50/50"
                  >
                    <ItemIcon className="h-3.5 w-3.5 text-gray-500" />
                    <span className="text-xs font-medium text-gray-700">
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* 카드 그리드: 그룹별 카드 */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {filteredGroups.map((group, groupIndex) => {
            const GroupIcon = group.groupIcon;
            return (
              <div
                key={groupIndex}
                className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/80 px-5 py-4">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-xl ${group.groupColor}`}
                  >
                    <GroupIcon className="h-5 w-5" />
                  </div>
                  <h2 className="text-base font-semibold text-gray-800">
                    {group.title}
                  </h2>
                </div>
                <div className="divide-y divide-gray-100">
                  {group.items.map((item, itemIndex) => {
                    const ItemIcon = item.icon;
                    return (
                      <button
                        key={itemIndex}
                        type="button"
                        onClick={() => handleMenuClick(item)}
                        className={`flex w-full items-center justify-between gap-3 px-5 py-3.5 text-left transition-colors hover:bg-gray-50
                          ${item.highlight ? "bg-red-50/50 hover:bg-red-50" : ""}
                        `}
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <div
                            className={`flex shrink-0 items-center justify-center rounded-lg p-1.5
                              ${item.highlight ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-500"}
                            `}
                          >
                            <ItemIcon className="h-4 w-4" />
                          </div>
                          <span
                            className={`truncate text-sm font-medium
                              ${item.highlight ? "text-red-700" : "text-gray-700"}
                            `}
                          >
                            {item.label}
                          </span>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            onClick={(e) => toggleFavorite(e, item.path)}
                            className="rounded p-1 transition-colors hover:bg-gray-200"
                            aria-label={favoritePaths.includes(item.path) ? "즐겨찾기 해제" : "즐겨찾기"}
                          >
                            {favoritePaths.includes(item.path) ? (
                              <HiStar className="h-5 w-5 text-yellow-400" />
                            ) : (
                              <HiOutlineStar className="h-5 w-5 text-gray-300 hover:text-yellow-400" />
                            )}
                          </button>
                          <HiChevronRight
                            className={`h-5 w-5 text-gray-300 transition-transform hover:translate-x-0.5
                              ${item.highlight ? "text-red-400" : ""}
                            `}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {filteredGroups.length === 0 && (
          <p className="py-12 text-center text-sm text-gray-500">
            검색 결과가 없습니다.
          </p>
        )}
      </div>
  );
};

export default AdminHome;
